import type { StorageBinShare, StorageDetail, StoragePalletShare } from './billing';
import { trNumber, trQuantity } from './pricing';
import type { LocationType } from './warehouse';

// Depolama by where the goods are kept (0067). A day's storage is worked out bin by bin, from every
// seller's stock in the bin at the end of the day:
//   - a measured product (en × boy × yükseklik) takes its own desi: units × desi;
//   - unmeasured products in a shelf or pallet bin whose volume is known (Depo tanımı → göz hacmi)
//     share what the measured goods in that bin (every seller's) leave free, by units: alone in the
//     bin they take all of it ("10 ürün bir palette ise bir palet"), next to a measured product the
//     rest, next to another seller's unmeasured product half of the rest for half of the units;
//   - an unmeasured product anywhere else (a bin with no volume given, an area such as the dock), or
//     in a bin the measured goods already fill on paper, counts the tariff's desi per unit
//     (default_unit_desi), as before;
//   - a pallet bin (bulk) is shared between the sellers in it by the space their goods take there
//     (as above), in thousandths of a pallet, so a pallet is charged once in total.
// Only a tariff priced per m³ per month charges unmeasured goods by the bin; one priced per desi per
// day counts them by the desi per unit, as it always did. Quantities are kept in thousandths (of a
// desi, of a pallet) as integers, so the shares of a bin add up exactly.

/** One seller's product in a bin at the end of the day. */
export interface BinStock {
  readonly tenantId: string;
  readonly productId: string;
  readonly qty: number;
  /** desi per unit (products.desi); null: the product has no measurements */
  readonly unitDesi: string | null;
}

/** A location with what every seller holds in it. */
export interface StorageBin {
  readonly code: string;
  readonly type: LocationType;
  /** the bin's volume; null: not given */
  readonly maxVolumeCm3: number | null;
  readonly stock: readonly BinStock[];
}

/** measured: its own desi · bin_share: a share of the bin's free volume · assumed: the desi per unit */
export type SpaceBasis = 'measured' | 'bin_share' | 'assumed';

export interface ItemSpace extends BinStock {
  readonly basis: SpaceBasis;
  /** thousandths of a desi */
  readonly milliDesi: bigint;
}

/** The space every product in a bin takes, in thousandths of a desi. */
export interface BinSpace {
  readonly code: string;
  readonly type: LocationType;
  /** the volume unmeasured goods may share (a shelf or pallet bin with a volume); null: none */
  readonly binMilli: bigint | null;
  /** the measured goods in the bin, every seller's */
  readonly measuredMilli: bigint;
  /** units of unmeasured products in the bin, every seller's */
  readonly unmeasuredUnits: number;
  /** the measured goods fill the bin on paper: its unmeasured goods count the desi per unit */
  readonly full: boolean;
  /** by seller, then product */
  readonly items: readonly ItemSpace[];
}

/** How a seller's tariff charges storage. */
export interface StorageBasis {
  /** ₺ / m³ / ay: unmeasured goods by the bin they are in; false (₺ / desi / gün): by the desi per unit */
  readonly byLocation: boolean;
  /** a pallet price: the seller's share of each pallet bin, not desi */
  readonly byPallet: boolean;
  /** desi per unmeasured unit where the bin does not decide (service_tariffs.default_unit_desi) */
  readonly assumedUnitDesi: string;
}

/** A seller's storage of a day: what the 'storage' and 'pallet_storage' charges book. */
export interface SellerStorage {
  readonly tenantId: string;
  /** desi (3 decimals) */
  readonly desi: string;
  /** pallets (3 decimals) */
  readonly pallets: string;
  /** how the desi came about */
  readonly desiDetail: StorageDetail;
  /** how the pallets came about */
  readonly palletDetail: StorageDetail;
}

/** desi per unit assumed for a seller with no tariff (it is not charged; its goods still take a share) */
const NO_TARIFF_UNIT_DESI = '1';

/** "2.00" → 2000n (thousandths, half up past the third decimal) */
export function toMilli(value: string): bigint {
  const m = /^(\d+)(?:\.(\d*))?$/.exec(value.trim());
  if (!m) throw new Error(`Geçersiz miktar: ${value}`);
  const fraction = (m[2] ?? '').padEnd(4, '0');
  const n = BigInt(m[1]!) * 1000n + BigInt(fraction.slice(0, 3));
  return Number(fraction[3]) >= 5 ? n + 1n : n;
}

/** 1571n → "1.571" */
export function fromMilli(n: bigint): string {
  return `${n / 1000n}.${String(n % 1000n).padStart(3, '0')}`;
}

/** A bin's volume in thousandths of a desi: cm³ ÷ 3000, half up. */
export function cm3ToMilliDesi(cm3: number): bigint {
  return (BigInt(Math.round(cm3)) * 2n + 3n) / 6n;
}

/**
 * `total` split by `weights` in whole parts that add up to it exactly (largest remainder; a tie goes
 * to the earlier weight). Nothing to split by (no weight above 0): all zero.
 */
export function shareOut(total: bigint, weights: readonly bigint[]): bigint[] {
  const sum = weights.reduce((a, w) => a + w, 0n);
  if (sum <= 0n || total <= 0n) return weights.map(() => 0n);
  const parts = weights.map((w) => (total * w) / sum);
  let left = total - parts.reduce((a, p) => a + p, 0n);
  const order = weights.map((w, i) => ({ i, rest: (total * w) % sum })).sort((a, b) => (a.rest === b.rest ? a.i - b.i : a.rest > b.rest ? -1 : 1));
  for (const { i } of order) {
    if (left <= 0n) break;
    parts[i]! += 1n;
    left -= 1n;
  }
  return parts;
}

const byOwner = (a: BinStock, b: BinStock) => (a.tenantId === b.tenantId ? (a.productId < b.productId ? -1 : a.productId > b.productId ? 1 : 0) : a.tenantId < b.tenantId ? -1 : 1);

/** Whether unmeasured goods in a location share its volume: a shelf or pallet bin with a volume. */
const sharesVolume = (bin: Pick<StorageBin, 'type' | 'maxVolumeCm3'>) => (bin.type === 'pick' || bin.type === 'bulk') && bin.maxVolumeCm3 !== null && bin.maxVolumeCm3 > 0;

/** The space every product in a bin takes (see the top of this file); `assumedUnitDesi` by seller. */
export function binSpace(bin: StorageBin, assumedUnitDesi: (tenantId: string) => string): BinSpace {
  const stock = bin.stock.filter((s) => s.qty > 0).sort(byOwner);
  const measuredMilli = stock.reduce((n, s) => (s.unitDesi === null ? n : n + BigInt(s.qty) * toMilli(s.unitDesi)), 0n);
  const unmeasured = stock.filter((s) => s.unitDesi === null);
  const unmeasuredUnits = unmeasured.reduce((n, s) => n + s.qty, 0);
  const binMilli = sharesVolume(bin) ? cm3ToMilliDesi(bin.maxVolumeCm3!) : null;
  const free = binMilli === null ? 0n : binMilli - measuredMilli;
  const byShare = binMilli !== null && unmeasuredUnits > 0 && free > 0n;
  const shares = byShare ? shareOut(free, unmeasured.map((s) => BigInt(s.qty))) : [];
  let next = 0;
  const items = stock.map((s): ItemSpace => {
    if (s.unitDesi !== null) return { ...s, basis: 'measured', milliDesi: BigInt(s.qty) * toMilli(s.unitDesi) };
    if (byShare) return { ...s, basis: 'bin_share', milliDesi: shares[next++]! };
    return { ...s, basis: 'assumed', milliDesi: BigInt(s.qty) * toMilli(assumedUnitDesi(s.tenantId)) };
  });
  return { code: bin.code, type: bin.type, binMilli, measuredMilli, unmeasuredUnits, full: binMilli !== null && unmeasuredUnits > 0 && free <= 0n, items };
}

/**
 * Each seller's share of a pallet bin in thousandths, adding up to 1000: by the space its goods take
 * there (binSpace), by units when nothing takes any (products measured at 0.00 desi).
 */
export function palletShares(space: BinSpace): Map<string, bigint> {
  const tenants = [...new Set(space.items.map((i) => i.tenantId))];
  const sumOf = (pick: (i: ItemSpace) => bigint) => tenants.map((t) => space.items.filter((i) => i.tenantId === t).reduce((n, i) => n + pick(i), 0n));
  let weights = sumOf((i) => i.milliDesi);
  if (weights.every((w) => w === 0n)) weights = sumOf((i) => BigInt(i.qty));
  const parts = shareOut(1000n, weights);
  return new Map(tenants.map((t, k) => [t, parts[k]!]));
}

interface Tally {
  byLocation: boolean;
  measuredUnits: number;
  measured: bigint;
  assumedUnits: number;
  assumed: bigint;
  unitDesi: string;
  full: string[];
  bins: StorageBinShare[];
  binsMilli: bigint;
  pallets: bigint;
  palletBins: StoragePalletShare[];
}

/**
 * Every seller's storage of a day from the bins they have stock in (with what the other sellers hold
 * there): desi by its tariff (StorageBasis) and its share of each pallet bin when the tariff has a
 * pallet price. Sellers not in `tariffs` take their share of a bin but are not returned.
 */
export function sellerStorage(bins: readonly StorageBin[], tariffs: ReadonlyMap<string, StorageBasis>): SellerStorage[] {
  const tally = new Map<string, Tally>();
  const of = (tenantId: string, basis: StorageBasis): Tally => {
    let t = tally.get(tenantId);
    if (!t) {
      t = {
        byLocation: basis.byLocation,
        measuredUnits: 0,
        measured: 0n,
        assumedUnits: 0,
        assumed: 0n,
        unitDesi: basis.assumedUnitDesi,
        full: [],
        bins: [],
        binsMilli: 0n,
        pallets: 0n,
        palletBins: [],
      };
      tally.set(tenantId, t);
    }
    return t;
  };
  for (const bin of [...bins].sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0))) {
    const space = binSpace(bin, (tenantId) => tariffs.get(tenantId)?.assumedUnitDesi ?? NO_TARIFF_UNIT_DESI);
    const sellers = [...new Set(space.items.map((i) => i.tenantId))];
    const pallets = bin.type === 'bulk' ? palletShares(space) : null;
    for (const tenantId of sellers) {
      const basis = tariffs.get(tenantId);
      if (!basis) continue;
      const t = of(tenantId, basis);
      const mine = space.items.filter((i) => i.tenantId === tenantId);
      const units = mine.reduce((n, i) => n + i.qty, 0);
      if (pallets && basis.byPallet) {
        const share = pallets.get(tenantId) ?? 0n;
        t.pallets += share;
        t.palletBins.push({ code: bin.code, units, share: fromMilli(share), sellers: sellers.length });
        continue;
      }
      let shareUnits = 0;
      let shareMilli = 0n;
      for (const item of mine) {
        if (item.basis === 'measured') {
          t.measuredUnits += item.qty;
          t.measured += item.milliDesi;
        } else if (item.basis === 'bin_share' && basis.byLocation) {
          shareUnits += item.qty;
          shareMilli += item.milliDesi;
        } else {
          t.assumedUnits += item.qty;
          t.assumed += BigInt(item.qty) * toMilli(basis.assumedUnitDesi);
          if (basis.byLocation && space.full && !t.full.includes(bin.code)) t.full.push(bin.code);
        }
      }
      if (shareUnits > 0) {
        t.binsMilli += shareMilli;
        t.bins.push({
          code: bin.code,
          binDesi: fromMilli(space.binMilli!),
          measuredDesi: fromMilli(space.measuredMilli),
          unmeasuredUnits: space.unmeasuredUnits,
          units: shareUnits,
          desi: fromMilli(shareMilli),
        });
      }
    }
  }
  return [...tally.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([tenantId, t]) => ({
      tenantId,
      desi: fromMilli(t.measured + t.binsMilli + t.assumed),
      pallets: fromMilli(t.pallets),
      desiDetail: {
        byLocation: t.byLocation,
        measured: t.measuredUnits > 0 ? { units: t.measuredUnits, desi: fromMilli(t.measured) } : null,
        assumed: t.assumedUnits > 0 ? { units: t.assumedUnits, unitDesi: t.unitDesi, desi: fromMilli(t.assumed), full: t.full } : null,
        bins: t.bins,
        pallets: [],
      },
      palletDetail: { byLocation: t.byLocation, measured: null, assumed: null, bins: [], pallets: t.palletBins },
    }));
}

// --- in words (the seller's Ücret dökümü, the charge's description) ------------------------------

const fullText = (codes: readonly string[]) => (codes.length === 1 ? `${codes[0]} gözü` : `${codes.slice(0, 3).join(', ')}${codes.length > 3 ? ` ve ${codes.length - 3} göz daha` : ''} gözleri`);

/**
 * What a storage line adds in its description when it is not by measured desi alone, e.g.
 * "ölçülü ürün 20 desi, ölçüsüz ürün göz payı 14,22 desi (2 göz), ölçüsüz ürün 5 adet × 1 desi";
 * for pallets the shared bins: "ortak palet gözü D-01-1-1: payınız 0,571". null: nothing to add.
 */
export function storageDetailSummary(detail: StorageDetail): string | null {
  const shared = detail.pallets.filter((p) => p.sellers > 1);
  if (shared.length > 0) {
    const list = shared.slice(0, 3).map((p) => `${p.code} ${trQuantity(p.share)}`);
    return `ortak palet ${shared.length === 1 ? 'gözünde' : 'gözlerinde'} payınız: ${list.join(', ')}${shared.length > 3 ? ` ve ${shared.length - 3} göz daha` : ''}`;
  }
  if (detail.bins.length === 0 && detail.assumed === null) return null;
  const parts: string[] = [];
  if (detail.measured) parts.push(`ölçülü ürün ${trQuantity(detail.measured.desi)} desi`);
  if (detail.bins.length > 0) {
    const desi = detail.bins.reduce((n, b) => n + toMilli(b.desi), 0n);
    parts.push(`ölçüsüz ürün göz payı ${trQuantity(fromMilli(desi))} desi (${trNumber(detail.bins.length)} göz)`);
  }
  if (detail.assumed) parts.push(`ölçüsüz ürün ${trNumber(detail.assumed.units)} adet × ${trQuantity(detail.assumed.unitDesi)} desi`);
  return parts.join(', ');
}

/** How a storage or pallet storage line was worked out, line by line, for the seller. */
export function storageDetailLines(detail: StorageDetail): string[] {
  const lines: string[] = [];
  if (detail.measured) {
    lines.push(`Ölçülü ürünler: ${trNumber(detail.measured.units)} adet, kendi ölçüleriyle (en × boy × yükseklik ÷ 3000) ${trQuantity(detail.measured.desi)} desi.`);
  }
  for (const b of detail.bins) {
    const free = toMilli(b.binDesi) - toMilli(b.measuredDesi);
    const head = `${b.code} gözü (hacmi ${trQuantity(b.binDesi)} desi)`;
    const room = toMilli(b.measuredDesi) > 0n ? `ölçülü ürünler ${trQuantity(b.measuredDesi)} desi kaplıyor, kalan ${trQuantity(fromMilli(free))} desi` : `gözün tamamı`;
    lines.push(
      b.units === b.unmeasuredUnits
        ? `${head}: ${room} buradaki ${trNumber(b.units)} adet ölçüsüz ürününüze düşer: ${trQuantity(b.desi)} desi.`
        : `${head}: ${room} bu gözdeki ${trNumber(b.unmeasuredUnits)} adet ölçüsüz ürüne adetle bölünür; sizin ${trNumber(b.units)} adet: ${trQuantity(b.desi)} desi.`,
    );
  }
  if (detail.assumed) {
    const a = detail.assumed;
    const count = `${trNumber(a.units)} adet × ${trQuantity(a.unitDesi)} desi = ${trQuantity(a.desi)} desi`;
    if (detail.byLocation === false) {
      // a tariff per desi per day: every unmeasured unit by the desi per unit, whatever bin it is in
      lines.push(`Ölçüsü girilmemiş ürünler, tarifenizdeki adet başına desiyle: ${count}.`);
    } else {
      const why = a.full.length > 0 ? ` ${fullText(a.full)} ölçülü ürünlerle dolu göründüğü için bu gözdekiler de böyle sayıldı.` : '';
      lines.push(`Ölçüsü girilmemiş, hacmi tanımlı bir raf ya da palet gözünde olmayan ürünler: ${count}.${why}`);
    }
  }
  for (const p of detail.pallets) {
    lines.push(
      p.sellers > 1
        ? `${p.code} palet gözü ${trNumber(p.sellers)} satıcıyla ortak: ürünlerinizin (${trNumber(p.units)} adet) kapladığı yere göre payınız ${trQuantity(p.share)} palet.`
        : `${p.code} palet gözü: yalnız sizin ürünleriniz (${trNumber(p.units)} adet), 1 palet.`,
    );
  }
  return lines;
}

/**
 * The rules under the seller's "Nasıl hesaplandı": how unmeasured goods are charged on the seller's
 * tariff (by the bin they are in, or by the desi per unit), and how a shared pallet bin is split.
 */
export function storageDetailNotes(detail: StorageDetail): string[] {
  const notes: string[] = [];
  const desi = detail.measured !== null || detail.assumed !== null || detail.bins.length > 0;
  if (desi && detail.byLocation !== false) {
    notes.push('Ölçüsü girilmemiş ürün durduğu gözün hacmiyle ücretlenir; ürünün ölçüsü girilince kendi desisiyle ücretlenir.');
  } else if (detail.assumed !== null) {
    notes.push('Tarifenizde ölçüsü girilmemiş ürün, durduğu göz ne olursa olsun adet başına desiyle ücretlenir; ürünün ölçüsü girilince kendi desisiyle ücretlenir.');
  }
  if (detail.pallets.some((p) => p.sellers > 1)) notes.push('Ortak palet gözü, ürünlerin kapladığı yere göre paylaştırılır.');
  return notes;
}
